import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { funnelsApi } from '../services/funnelsApi';
import type {
  CreateFunnelRequest,
  UpdateFunnelRequest,
  DeleteFunnelRequest,
  FunnelQuery,
  FunnelResponse,
  ReorderFunnelsRequest
} from '@troia-v3/types';
import { toast } from 'sonner';

/**
 * Query Keys for Funnels
 */
export const FUNNEL_KEYS = {
  all: ['funnels'] as const,
  lists: () => [...FUNNEL_KEYS.all, 'list'] as const,
  list: (query?: FunnelQuery) => [...FUNNEL_KEYS.lists(), query] as const,
  details: () => [...FUNNEL_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...FUNNEL_KEYS.details(), id] as const,
  dropdown: () => [...FUNNEL_KEYS.all, 'dropdown'] as const,
  active: () => [...FUNNEL_KEYS.all, 'active'] as const,
  /** Lista COMPLETA (todas as páginas concatenadas) — ver `useAllFunnels`. */
  allPages: (query?: AllFunnelsQuery) => [...FUNNEL_KEYS.lists(), 'all-pages', query] as const,
};

/** Filtros aceitos por `useAllFunnels` — a paginação é responsabilidade do hook. */
export type AllFunnelsQuery = Omit<FunnelQuery, 'page' | 'limit'>;

/** Teto do backend em `getFunnelsQuerySchema` (`limit: max(100)`). */
const FUNNELS_PAGE_SIZE = 100;

/**
 * Trava de segurança: 20 páginas = 2.000 funis. Um tenant com mais que isso
 * indica lixo (foi o caso do dev, com 121 funis de E2E) e não vale disparar
 * dezenas de requisições numa tela de configuração.
 */
const FUNNELS_MAX_PAGES = 20;

/**
 * Hook to fetch all funnels with pagination
 */
export const useFunnels = (query?: FunnelQuery) => {
  return useQuery({
    queryKey: FUNNEL_KEYS.list(query),
    queryFn: () => funnelsApi.getFunnels(query),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Lista TODOS os funis do tenant, percorrendo as páginas da rota paginada.
 *
 * Por que existe: `GET /funnels` tem teto de 100 por página (Zod do backend) e
 * a tela de configuração de funis é uma lista master com busca no cliente —
 * com `limit: 100` fixo, quem tinha 128 funis simplesmente não via 28 deles e
 * a busca não os encontrava, sem nenhum aviso (RD5 da Fase C2).
 *
 * A primeira página vem sozinha (é ela que informa `totalPages`); as demais
 * vão em paralelo. Concatenação por laço — nunca `push(...pagina)`, que
 * estoura a pilha em lista grande (CLAUDE.md NUNCA #89).
 */
export const useAllFunnels = (query?: AllFunnelsQuery) => {
  return useQuery({
    queryKey: FUNNEL_KEYS.allPages(query),
    queryFn: async (): Promise<FunnelResponse[]> => {
      const first = await funnelsApi.getFunnels({ ...query, page: 1, limit: FUNNELS_PAGE_SIZE });
      const items: FunnelResponse[] = [];
      for (const funnel of first.items) items.push(funnel);

      const totalPages = Math.min(first.totalPages ?? 1, FUNNELS_MAX_PAGES);
      if (totalPages <= 1) return items;

      const restPages: number[] = [];
      for (let page = 2; page <= totalPages; page += 1) restPages.push(page);

      const rest = await Promise.all(
        restPages.map((page) => funnelsApi.getFunnels({ ...query, page, limit: FUNNELS_PAGE_SIZE })),
      );
      for (const pageResult of rest) {
        for (const funnel of pageResult.items) items.push(funnel);
      }
      return items;
    },
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook to fetch funnel by ID (uses dropdown route - no module permissions required)
 */
export const useFunnel = (id: string | undefined) => {
  return useQuery({
    queryKey: FUNNEL_KEYS.detail(id!),
    queryFn: () => funnelsApi.getFunnelByIdForDropdown(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook to fetch funnels for dropdown.
 *
 * `options.enabled` é opcional (default `true`) para não quebrar os chamadores
 * existentes — passe `{ enabled: isOpen }` em modais/drawers (CLAUDE.md NUNCA #14).
 */
export const useFunnelsDropdown = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: FUNNEL_KEYS.dropdown(),
    queryFn: () => funnelsApi.getFunnelsForDropdown(),
    staleTime: 10 * 60 * 1000, // 10 minutes - dropdown data changes less frequently
    enabled: options?.enabled ?? true,
  });
};

/**
 * Steps of a funnel for dropdowns (cross-module safe — /funnel-steps/by-funnel/:funnelId).
 * enabled only when funnelId is set (lazy fetching rule).
 */
export const useFunnelStepsDropdown = (funnelId?: string) => {
  return useQuery({
    queryKey: ['funnel-steps-dropdown', funnelId],
    queryFn: () => funnelsApi.getStepsByFunnelId(funnelId as string),
    enabled: !!funnelId,
    staleTime: 10 * 60 * 1000,
  });
};

/**
 * Hook to fetch active funnels
 */
export const useActiveFunnels = () => {
  return useQuery({
    queryKey: FUNNEL_KEYS.active(),
    queryFn: () => funnelsApi.getActiveFunnels(),
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook to create funnel
 */
export const useCreateFunnel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateFunnelRequest) => funnelsApi.createFunnel(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FUNNEL_KEYS.all });
      toast.success('Funil criado com sucesso');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao criar funil');
    },
  });
};

/**
 * Hook to update funnel
 */
export const useUpdateFunnel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateFunnelRequest }) =>
      funnelsApi.updateFunnel(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: FUNNEL_KEYS.all });
      queryClient.invalidateQueries({ queryKey: FUNNEL_KEYS.detail(variables.id) });
      toast.success('Funil atualizado com sucesso');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao atualizar funil');
    },
  });
};

/**
 * Exclui um funil, opcionalmente decidindo o destino dos leads vinculados
 * (`{ leads: { action: 'delete' } }` ou `{ leads: { action: 'transfer', … } }`).
 *
 * Sem `body`, o backend mantém o comportamento antigo: funil COM leads é
 * recusado com 400 — quem oferece a escolha é o `FunnelDeleteModal`.
 */
export const useDeleteFunnel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body?: DeleteFunnelRequest }) =>
      funnelsApi.deleteFunnel(id, body),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: FUNNEL_KEYS.all });
      // Os leads mudaram de funil/etapa (ou sumiram) — as listagens do CRM e as
      // views que agregam atividades do lead precisam ser refeitas.
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['funnel-steps-dropdown'] });
      queryClient.invalidateQueries({ queryKey: ['funnels-with-steps-dropdown'] });
      if (result.leadsAction === 'transfer') {
        toast.success(
          `Funil excluído — ${result.leadsAffected} lead(s) transferido(s) para o funil escolhido`,
        );
      } else if (result.leadsAction === 'delete') {
        toast.success(`Funil excluído — ${result.leadsAffected} lead(s) excluído(s) junto`);
      } else {
        toast.success('Funil excluído com sucesso');
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao excluir funil');
    },
  });
};

/**
 * Versão imperativa de `useValidateDeleteFunnel`: devolve uma função que
 * consulta `GET /funnels/validate-delete/:id` sob demanda (clique em
 * "Excluir"), reusando a MESMA chave de cache do hook declarativo.
 *
 * Existe porque a decisão "confirmação simples × modal com destino dos leads"
 * depende de `leadCount`, e o hook declarativo só roda com o id já em estado.
 */
export const useValidateDeleteFunnelLazy = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (id: string) =>
      queryClient.fetchQuery({
        queryKey: [...FUNNEL_KEYS.detail(id), 'validate-delete'],
        queryFn: () => funnelsApi.validateDeleteFunnel(id),
        staleTime: 0,
      }),
    [queryClient],
  );
};

/**
 * Hook to delete funnel with validation
 */
export const useDeleteFunnelWithValidation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => funnelsApi.deleteFunnelWithValidation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FUNNEL_KEYS.all });
      toast.success('Funil excluído com sucesso');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao excluir funil');
    },
  });
};

/**
 * Hook to validate funnel deletion
 */
export const useValidateDeleteFunnel = (id: string | undefined) => {
  return useQuery({
    queryKey: [...FUNNEL_KEYS.detail(id!), 'validate-delete'],
    queryFn: () => funnelsApi.validateDeleteFunnel(id!),
    enabled: !!id,
    staleTime: 0, // Always fresh for validation
  });
};

/**
 * Hook to reorder funnels
 */
export const useReorderFunnels = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ReorderFunnelsRequest) => funnelsApi.reorderFunnels(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FUNNEL_KEYS.all });
      toast.success('Funis reordenados com sucesso');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao reordenar funis');
    },
  });
};
